/**
 * SKU Velocity scatter — a purpose-built canvas engine, NOT an adaptation of `field/plot.ts`
 * (that engine's log/dual-axis/zoom/marquee/cluster machinery doesn't fit this page's much
 * simpler encoding: x is each SKU's own velocity, y is stock on hand, color is band).
 *
 * Built for ~30k real points per the plan: per-SKU screen positions are computed ONCE in
 * `setPopulation`/`resize` (they don't change on a lever drag); a drag only recomputes the
 * claim map (`lib/data/scenario.ts`'s `buildClaimMap`, done by the caller) and calls `paint`,
 * which repaints in COHORT BATCHES — one `beginPath`/`fill` per (band × claimed?) group, not
 * per point, using `moveTo` before each `arc` so circles don't chain into stray fill slivers.
 *
 * x-encoding: D gets a narrow column (all velocity 0), C/B linear within their REAL observed
 * range, A logarithmic within its REAL observed range — computed off the loaded population,
 * not a hardcoded canon range, so the columns use their full width whatever the real
 * distribution turns out to be. The relocate threshold's line is positioned by DIAL VALUE
 * (0–24 picks/yr → a fraction of the C/B columns), the same shape as the prototype's
 * `thresholdF`; which SKUs the lever actually claims comes from `buildClaimMap`'s real
 * per-family membership, not a raw velocity comparison — see this module's caller
 * (`velocity-scatter.tsx`) for the reconciliation note.
 */

import type { Band, LeverCause, Sku } from "@/lib/data";

import type { FilterState } from "./state";

/** Column bounds on the plot's 0–1 x-axis, left to right: dead stock, slow, medium, fast. */
const XL: Record<Band, [number, number]> = {
  D: [0, 0.1],
  C: [0.1, 0.4],
  B: [0.4, 0.7],
  A: [0.7, 1.0],
};

/** Deterministic pseudo-random in [0, 1) off an integer id — used only for visual jitter within
 *  a column, never for anything the model counts. */
function hash01(id: number): number {
  let x = (id * 2654435761) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 2246822519) >>> 0;
  x ^= x >>> 16;
  return (x >>> 0) / 4294967295;
}

type BandRange = Record<Band, [number, number]>;

function velX(band: Band, v: number, jitter: number, range: BandRange): number {
  const [a, b] = XL[band];
  if (band === "D") {
    return a + (0.15 + jitter * 0.7) * (b - a);
  }
  const [lo, hi] = range[band];
  let f: number;
  if (hi <= lo) {
    f = 0.5;
  } else if (band === "A" && lo > 0) {
    f = Math.log(v / lo) / Math.log(hi / lo);
  } else {
    f = (v - lo) / (hi - lo);
  }
  f = Math.min(0.98, Math.max(0.02, f + (jitter - 0.5) * 0.05));
  return a + f * (b - a);
}

/** The relocate dial's line position, by DIAL VALUE (not a per-SKU lookup) — 0 sits at the end
 *  of the D column, 11 (the safe stop) at the end of C, beyond that partway into B. */
export function thresholdFraction(threshold: number): number {
  if (threshold <= 0) {
    return XL.D[1];
  }
  if (threshold <= 11) {
    return XL.C[0] + (threshold / 12) * (XL.C[1] - XL.C[0]);
  }
  return XL.B[0] + Math.min(1, (threshold - 11) / 13) * (XL.B[1] - XL.B[0]);
}

export interface ScatterColors {
  A: string;
  B: string;
  C: string;
  D: string;
  dim: string;
  line: string;
  muted: string;
  red: string;
  ink: string;
}

export interface PaintOptions {
  claim: ReadonlyMap<number, LeverCause>;
  filter: FilterState;
  placements: ReadonlyMap<number, { system: string; misslot: boolean }>;
  reloThreshold: number;
  colors: ScatterColors;
}

interface HitResult {
  sku: Sku;
  cause: LeverCause;
  system?: string;
  misslot?: boolean;
}

const PAD_L = 46;
const PAD_R = 10;
const PAD_T = 10;
const PAD_B = 22;

export class VelocityScatterEngine {
  private readonly ctx: CanvasRenderingContext2D;
  private skus: readonly Sku[] = [];
  private xFrac = new Float32Array(0);
  private yFrac = new Float32Array(0);
  private screenX = new Float32Array(0);
  private screenY = new Float32Array(0);
  private plotW = 0;
  private plotH = 0;
  private width = 0;
  private height = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("2d canvas context unavailable");
    }
    this.ctx = ctx;
  }

  /** Called once per SKU population (or whenever `invMax` changes) — everything after this is
   *  cheap. */
  setPopulation(skus: readonly Sku[], invMax: number): void {
    this.skus = skus;
    const n = skus.length;
    this.xFrac = new Float32Array(n);
    this.yFrac = new Float32Array(n);
    const range: BandRange = {
      A: [Infinity, -Infinity],
      B: [Infinity, -Infinity],
      C: [Infinity, -Infinity],
      D: [0, 0],
    };
    for (const s of skus) {
      if (s.band === "D") {
        continue;
      }
      const r = range[s.band];
      if (s.vel < r[0]) {
        r[0] = s.vel;
      }
      if (s.vel > r[1]) {
        r[1] = s.vel;
      }
    }
    for (const band of ["A", "B", "C"] as Band[]) {
      if (range[band][1] < range[band][0]) {
        range[band] = [0, 1];
      }
    }
    for (let i = 0; i < n; i++) {
      const s = skus[i] as Sku;
      const jitter = hash01(s.id);
      this.xFrac[i] = velX(s.band, s.vel, jitter, range);
      this.yFrac[i] = Math.max(0, Math.min(1, invMax > 0 ? s.inv / invMax : 0));
    }
    this.screenX = new Float32Array(n);
    this.screenY = new Float32Array(n);
    this.resize();
  }

  /** Recomputes screen positions off the canvas's current box — cheap even at 30k points, and
   *  the only thing a container resize needs. */
  resize(): boolean {
    const wrap = this.canvas.parentElement;
    if (!wrap) {
      return false;
    }
    const rect = wrap.getBoundingClientRect();
    if (rect.width < 50 || rect.height < 40) {
      return false;
    }
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.width = rect.width;
    this.height = rect.height;
    this.plotW = this.width - PAD_L - PAD_R;
    this.plotH = this.height - PAD_T - PAD_B;
    const n = this.skus.length;
    for (let i = 0; i < n; i++) {
      this.screenX[i] = PAD_L + (this.xFrac[i] ?? 0) * this.plotW;
      this.screenY[i] = PAD_T + this.plotH - (this.yFrac[i] ?? 0) * this.plotH;
    }
    return true;
  }

  /** The one repaint entry point — axes/gridlines/threshold line, then every point in COHORT
   *  BATCHES keyed by `${band}|${claimed?}|${inFilter?}`, one `beginPath`/`fill` each. */
  paint(opts: PaintOptions): void {
    const { ctx, plotW, plotH, width: W, height: H } = this;
    if (plotW <= 0 || plotH <= 0) {
      return;
    }
    const { claim, filter, placements, reloThreshold, colors } = opts;
    ctx.clearRect(0, 0, W, H);
    const X = (f: number) => PAD_L + f * plotW;

    (["D", "C", "B", "A"] as Band[]).forEach((bk, i) => {
      ctx.fillStyle = i % 2 === 0 ? "rgba(0,0,0,0.016)" : "rgba(0,0,0,0)";
      ctx.fillRect(X(XL[bk][0]), PAD_T, (XL[bk][1] - XL[bk][0]) * plotW, plotH);
    });
    ctx.strokeStyle = colors.line;
    ctx.lineWidth = 1;
    [0, 0.1, 0.4, 0.7, 1].forEach((f) => {
      ctx.beginPath();
      ctx.moveTo(X(f), PAD_T);
      ctx.lineTo(X(f), PAD_T + plotH);
      ctx.stroke();
    });
    ctx.font = "11px sans-serif";
    ctx.fillStyle = colors.muted;
    ctx.textAlign = "center";
    const bandLabel: Record<Band, string> = {
      D: "D · 0",
      C: "C · slow",
      B: "B · medium",
      A: "A · fast",
    };
    (["D", "C", "B", "A"] as Band[]).forEach((bk) => {
      ctx.fillText(bandLabel[bk], X((XL[bk][0] + XL[bk][1]) / 2), PAD_T + plotH + 15);
    });

    // Relocate zone + line, positioned by dial value (see module doc's reconciliation note).
    if (reloThreshold >= 0) {
      const tf = thresholdFraction(reloThreshold);
      ctx.fillStyle = "rgba(235,23,0,0.05)";
      ctx.fillRect(PAD_L, PAD_T, tf * plotW, plotH);
      ctx.strokeStyle = colors.red;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(X(tf), PAD_T);
      ctx.lineTo(X(tf), PAD_T + plotH);
      ctx.stroke();
    }

    // ── cohort-batched points ──
    const n = this.skus.length;
    const cohorts = new Map<string, { color: string; alpha: number; r: number; idx: number[] }>();
    for (let i = 0; i < n; i++) {
      const s = this.skus[i] as Sku;
      const claimed = claim.has(s.id);
      let inFilter = true;
      if (filter) {
        const p = placements.get(s.id);
        inFilter = !!p && p.system === filter.sys && (!filter.mis || p.misslot);
      }
      let color: string;
      let alpha: number;
      let r: number;
      if (claimed) {
        color = colors.dim;
        alpha = 0.12;
        r = 1.5;
      } else {
        color = colors[s.band];
        alpha = 0.6;
        r = 2.1;
      }
      if (filter) {
        alpha = inFilter ? (claimed ? 0.25 : 0.9) : 0.05;
        r = inFilter ? 2.6 : 1.5;
      }
      const key = `${color}|${alpha}|${r}`;
      let cohort = cohorts.get(key);
      if (!cohort) {
        cohort = { color, alpha, r, idx: [] };
        cohorts.set(key, cohort);
      }
      cohort.idx.push(i);
    }
    for (const cohort of cohorts.values()) {
      ctx.globalAlpha = cohort.alpha;
      ctx.fillStyle = cohort.color;
      ctx.beginPath();
      for (const i of cohort.idx) {
        const x = this.screenX[i] ?? 0;
        const y = this.screenY[i] ?? 0;
        // `moveTo` before each `arc` starts a fresh subpath — without it, `fill()` can paint a
        // thin sliver connecting one circle to the next.
        ctx.moveTo(x + cohort.r, y);
        ctx.arc(x, y, cohort.r, 0, Math.PI * 2);
      }
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    if (reloThreshold >= 0) {
      const tf = thresholdFraction(reloThreshold);
      const label = reloThreshold === 0 ? "dead stock" : `≤ ${reloThreshold} picks/yr`;
      ctx.font = "9.5px sans-serif";
      const tw = ctx.measureText(label).width;
      const bx = Math.min(X(tf) + 4, PAD_L + plotW - tw - 10);
      ctx.fillStyle = colors.red;
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(bx, PAD_T + 3, tw + 8, 14, 2);
      } else {
        ctx.rect(bx, PAD_T + 3, tw + 8, 14);
      }
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.textAlign = "left";
      ctx.fillText(label, bx + 4, PAD_T + 13);
    }
  }

  /** Nearest point within radius, for the hover tooltip. Linear scan — cheap even at 30k points
   *  for a single mousemove. */
  hitTest(
    mx: number,
    my: number,
    placements: ReadonlyMap<number, { system: string; misslot: boolean }>,
    claim: ReadonlyMap<number, LeverCause>,
  ): HitResult | null {
    let best = -1;
    let bestD = 64; // 8px radius²
    const n = this.skus.length;
    for (let i = 0; i < n; i++) {
      const dx = (this.screenX[i] ?? 0) - mx;
      const dy = (this.screenY[i] ?? 0) - my;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best < 0) {
      return null;
    }
    const sku = this.skus[best] as Sku;
    const placement = placements.get(sku.id);
    return {
      sku,
      cause: claim.get(sku.id) ?? null,
      system: placement?.system,
      misslot: placement?.misslot,
    };
  }
}
